import { useEffect, useState } from 'react';
import { 
  Bell, Check, CheckCheck, AlertTriangle, Package, 
  CreditCard, Shield, Settings, RefreshCw, X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import api from '@/lib/api';

const categoryIcons = {
  order: Package,
  inventory: AlertTriangle,
  payment: CreditCard,
  security: Shield,
  system: Settings,
  marketing: Bell,
  customer: Bell
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-800',
  normal: 'bg-blue-100 text-blue-800',
  high: 'bg-yellow-100 text-yellow-800',
  critical: 'bg-red-100 text-red-800'
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchNotifications();
  }, [filter]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter === 'unread') params.append('unread_only', 'true');
      
      const res = await api.get(`/enterprise/notifications?${params}`);
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await api.put(`/enterprise/notifications/${notificationId}/read`);
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/enterprise/notifications/read-all');
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  return (
    <div className="min-h-screen pb-16" data-testid="notifications-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Notifications</h1>
            <p className="text-stone-500 mt-1">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="unread">Unread only</SelectItem>
              </SelectContent>
            </Select>
            {unreadCount > 0 && (
              <Button variant="outline" onClick={markAllAsRead}>
                <CheckCheck className="h-4 w-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-stone-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-16">
                <Bell className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                <p className="text-stone-500">No notifications</p>
              </div>
            ) : (
              <div className="divide-y">
                {notifications.map((notification) => {
                  const Icon = categoryIcons[notification.category] || Bell;
                  return (
                    <div 
                      key={notification.notification_id}
                      className={`p-4 flex items-start gap-4 hover:bg-stone-50 transition-colors ${
                        !notification.is_read ? 'bg-blue-50/50' : ''
                      }`}
                    >
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        notification.type === 'alert' ? 'bg-red-100' :
                        notification.type === 'warning' ? 'bg-yellow-100' :
                        notification.type === 'success' ? 'bg-green-100' :
                        'bg-stone-100'
                      }`}>
                        <Icon className={`h-5 w-5 ${
                          notification.type === 'alert' ? 'text-red-600' :
                          notification.type === 'warning' ? 'text-yellow-600' :
                          notification.type === 'success' ? 'text-green-600' :
                          'text-stone-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-stone-900">
                            {notification.title}
                          </p>
                          <Badge className={priorityColors[notification.priority]}>
                            {notification.priority}
                          </Badge>
                          {!notification.is_read && (
                            <span className="h-2 w-2 rounded-full bg-blue-500" />
                          )}
                        </div>
                        <p className="text-sm text-stone-600 mt-1">
                          {notification.message}
                        </p>
                        <div className="flex items-center gap-4 mt-2">
                          <span className="text-xs text-stone-400">
                            {new Date(notification.created_at).toLocaleString()}
                          </span>
                          {notification.action_url && (
                            <a 
                              href={notification.action_url}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              View details →
                            </a>
                          )}
                        </div>
                      </div>
                      {!notification.is_read && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => markAsRead(notification.notification_id)}
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
